import { useEffect, useState } from 'react';
import { adminApi } from '../../services/portalApi';
import { fmtDateTime } from '../../utils/date';
import type { MonthlyReport } from '../../types';

function fmtDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function Reports() {
  const now = new Date();
  const [monthStr, setMonthStr] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  function loadReport(ms: string) {
    const [y, m] = ms.split('-').map(Number);
    if (!y || !m) return;
    setLoading(true);
    setError('');
    adminApi.monthlyReport(y, m)
      .then(setReport)
      .catch(() => setError('Failed to load report.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadReport(monthStr);
  }, []);

  function handleMonthChange(e: React.ChangeEvent<HTMLInputElement>) {
    setMonthStr(e.target.value);
    loadReport(e.target.value);
  }

  const monthLabel = report
    ? new Date(report.year, report.month - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="px-6 py-6 max-w-5xl">
      <style>{`@media print { aside { display: none } nav { display: none } .no-print { display: none } }`}</style>

      <div className="mb-4 no-print">
        <h1 className="text-lg font-semibold text-gray-900">Monthly Report</h1>
        {report && !loading && (
          <p className="text-sm text-gray-500">{monthLabel}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 no-print">
        <input
          type="month"
          value={monthStr}
          onChange={handleMonthChange}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <button
          onClick={() => window.print()}
          className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Print
        </button>
      </div>

      {loading && <div className="text-center py-12 text-gray-400">Loading report…</div>}
      {error && <div className="text-red-600 py-4">{error}</div>}

      {report && !loading && (
        <div className="space-y-6">
          {/* Print header */}
          <div className="hidden print:block mb-4">
            <h2 className="text-xl font-bold">PatrolIt — Monthly Report</h2>
            <p className="text-gray-600">{monthLabel}</p>
          </div>

          {/* Summary cards */}
          <section>
            <h2 className="font-semibold text-gray-700 mb-3">{monthLabel} — Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Patrols', value: report.summary.total_patrols },
                { label: 'Alarms', value: report.summary.total_alarms },
                { label: 'Incidents', value: report.summary.total_incidents },
                { label: 'Km Patrolled', value: `${Number(report.summary.total_km).toFixed(1)} km` },
                { label: 'Patrol Hours', value: fmtDuration(report.summary.total_duration_min) },
              ].map((card) => (
                <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                  <div className="text-xs text-gray-500 mt-1">{card.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Guard breakdown */}
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-700 mb-3">By Patroller</h2>
            {report.by_guard.length === 0 ? (
              <p className="text-sm text-gray-400">No patrols this month.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200">
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Patroller</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Patrols</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Alarms</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Incidents</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">km</th>
                      <th className="pb-2 font-semibold text-gray-600">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_guard.map((g) => (
                      <tr key={g.guard_id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium text-gray-900">{g.guard_name}</td>
                        <td className="py-2 pr-4 text-gray-700">{g.patrols}</td>
                        <td className="py-2 pr-4">
                          {g.alarms > 0
                            ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{g.alarms}</span>
                            : <span className="text-gray-400 text-xs">0</span>}
                        </td>
                        <td className="py-2 pr-4">
                          {g.incidents > 0
                            ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">{g.incidents}</span>
                            : <span className="text-gray-400 text-xs">0</span>}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{Number(g.km).toFixed(2)}</td>
                        <td className="py-2 text-gray-600">{fmtDuration(g.duration_min)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Patrol Reports */}
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-700 mb-3">
              Patrol Reports ({report.incidents.length})
            </h2>
            {report.incidents.length === 0 ? (
              <p className="text-sm text-gray-400">No patrol incident reports this month.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200">
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Patroller</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Patrol End</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Type</th>
                      <th className="pb-2 font-semibold text-gray-600">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.incidents.map((inc) => (
                      <tr key={inc.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium text-gray-900 flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 inline-block" style={{ background: inc.colour ?? '#888' }} />
                          {inc.guard_name}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{fmtDateTime(inc.started_at)}</td>
                        <td className="py-2 pr-4">
                          {inc.patrol_type
                            ? <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{inc.patrol_type}</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-2 text-gray-700 max-w-xs whitespace-pre-wrap">{inc.incident_notes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Alarms */}
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-700 mb-3">
              Alarms ({report.alarms.length})
            </h2>
            {report.alarms.length === 0 ? (
              <p className="text-sm text-gray-400">No alarms this month.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200">
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Patroller</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Time</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Location</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Patroller report</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Control room</th>
                      <th className="pb-2 font-semibold text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.alarms.map((a) => (
                      <tr key={a.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium text-gray-900">{a.guard_name}</td>
                        <td className="py-2 pr-4 text-gray-600">{fmtDateTime(a.raised_at)}</td>
                        <td className="py-2 pr-4 text-gray-500 text-xs font-mono">
                          {a.lat.toFixed(5)}, {a.lng.toFixed(5)}
                        </td>
                        {/* Two columns, not one: what the patroller saw is the
                            record a committee or an insurer reads; the control
                            room's note is what was done about it. */}
                        <td className="py-2 pr-4 text-gray-700 max-w-xs whitespace-pre-wrap">{a.notes ?? '—'}</td>
                        <td className="py-2 pr-4 text-gray-600 max-w-xs whitespace-pre-wrap">{a.ack_notes ?? '—'}</td>
                        <td className="py-2">
                          {a.ack_at ? (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                              Acked {fmtDateTime(a.ack_at)}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                              Unacknowledged
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          {/* Incidents */}
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-700 mb-3">
              Incidents ({report.incident_events.length})
            </h2>
            {report.incident_events.length === 0 ? (
              <p className="text-sm text-gray-400">No incidents this month.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200">
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Patroller</th>
                      <th className="pb-2 pr-4 font-semibold text-gray-600">Time</th>
                      <th className="pb-2 font-semibold text-gray-600">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.incident_events.map((ie) => (
                      <tr key={ie.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium text-gray-900">{ie.guard_name}</td>
                        <td className="py-2 pr-4 text-gray-600">{fmtDateTime(ie.raised_at)}</td>
                        <td className="py-2 text-gray-700 max-w-xs whitespace-pre-wrap">{ie.notes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

        </div>
      )}
    </div>
  );
}
